import React, { useEffect, useState } from 'react';
import { Alert, Grid, Stack } from '@mui/material';
import { DeleteForeverOutlined } from '@mui/icons-material';
import { Button, ButtonColorProps, ButtonVariantProps } from '../../../../../components/atoms/Button';
import { TextField, TextFieldTypeProps } from '../../../../../components/atoms/TextField';
import { ImagePopup, ImagePopupSlide } from '../../../../../components/atoms/ImagePopup';
import { PartTypeActionSearch } from '../../../../../components/molecules/PartTypeActionSearch';
import { PartTypeActionNote } from '../../../../../components/molecules/PartTypeActionNote';
import { PartTypeLocationItemData } from '../../../../../components/molecules/PartTypeLocationItem';
import { PartLocationSelectPopup } from '../../../../../components/organisms/PartLocationSelectPopup';
import { useGetUploadedImages } from '../../../../../queries/uploadedfiles-query';
import { CustomPermission, IconColorProps, PartTypeActionLiveSearchItem, PartTypeActionLiveSearchItemCategoryCode } from '../../../../../@types';
import { Folders } from '../../../../../@types/uploaded-files.type';
import { isEmptyString } from '../../../../../utils/common';
import { PartActionDoaCardProps } from './PartActionDoaCard.props';
import { 
  DefaultCard, 
  StyledCardContent,
  CardTitle,
  CloseWrapper,
  SelectButton,
  InnerWrapper,
  OuterWrapper
} from '../PartAction.styles';
import { useGetPartLocationDataByServiceId } from '../../../../../queries/part-query';
import { PartActionType, PartActionValidateType } from '../../../../../@types/partAction.type';
import { partActionConditionValidation } from '../../../../../utils/part-action';
import { PartConditionOptions } from '../../../../../@types/part.type';
import { LOCATION_CANNOT_EMPTY } from '../../../../../constants/partAction';
import { useSubLocationsForCreateServices } from '../../../../../queries/live-search-query';
import { useGetUserSystemPermissions } from '../../../../../queries/user-query';
import { FormInterceptor } from '../FormInterceptor';
import { blockInteractionAvailable, getBlockInteractionMessage, getFormIsRepeatableStatus, validateInteractionForms } from '../../../../../utils/interactions';
import { MerchantDamagePartSerialMenuData } from '../../../../../@types/part.type';
import { Loader } from '../../../../../components/atoms/Loader';
import { Typography, TypographyVariantProps } from '../../../../../components/atoms/Typography';
import { COLORS } from '../../../../../configs/colors';

export const PartActionDoaCard: React.FC<PartActionDoaCardProps> = ({
  service,
  value,
  helperText,
  onChange,
  onClose,
  setHelperText,
  isMobileView,
  interceptedFormsObject,
  setInterceptedFormsObject,
  repeatableFormData,
  setRepeatableFormData,
  setMerchantDamageObjectParcer,
  partActionData,
  isInteractionSubmiting,
  submitInteractionForms
}) => {
  const [isSearchMode, setIsSearchMode] = useState(true);
  const [isOutSerialized, setIsOutSerialized] = useState(false);
  const [showOutSerialSearch, setShowOutSerialSearch] = useState(false);
  const [isSelectOutLocationPopup, setIsSelectOutLocationPopup] = useState(false);
  const [outPartImages, setOutPartImages] = useState<Array<ImagePopupSlide>>([]);
  const [siteHierarchyWarning, setSiteHierarchyWarning] = useState<string>('');
  const [underClientStructureWarning, setUnderClientStructureWarning] = useState<string>('');
  const [permissions, setPermissions] = useState<CustomPermission[]>();
  const [locationInteractionSubmitting, setLocationInteractionSubmitting] = useState(false);

  const outValueUploadedImagesQuery = useGetUploadedImages(value?.outValue?.partTypeId || 0, Folders.PartTypesImages);
  const outPartLocationDataByServiceIdQuery = useGetPartLocationDataByServiceId(value?.outValue?.partTypeId || 0, service.id);
  const subLocationsForCreateServicesQuery = useSubLocationsForCreateServices();
  const getUserPermissionsQuery = useGetUserSystemPermissions();

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    const outValidations = async () => {
      if (value?.outValue?.isInSiteHierarchy === false && value?.outValue?.serial) {
        const subLocationsResult = await subLocationsForCreateServicesQuery.mutateAsync(value.outValue?.locationId);
        const main = subLocationsResult?.find(subLocation => subLocation.isMain);
    
        const partParentNode = main ? `${main.name} /` : '';
        const serviceBaseNode = service.subLocation ? `/ ${service.subLocation}` : '';

        setSiteHierarchyWarning(`Warning: ${value?.outValue?.serial} is not at ${service.mainLocation} ${serviceBaseNode}, but at ${partParentNode} ${value.outValue?.location}. This is an unexpected part location; please confirm the correct part has been selected prior to proceeding. installed in ${value?.outValue?.name}: ${value?.outValue?.serial}`);
      } else {
        setSiteHierarchyWarning('');
      } 
      
      if (value?.outValue?.isUnderClientStructure === false && value?.outValue?.serial) {
        const baseOrParentnode = service.subLocation ? service.subLocation : service.mainLocation;
        setUnderClientStructureWarning(`Warning: ${value?.outValue?.serial} is not at ${baseOrParentnode}, but at ${value.outValue?.location}.  Please confirm the correct part has been selected prior to proceeding.`);
      } else {
        setUnderClientStructureWarning('');
      }
    };

    outValidations();
  }, [value?.outValue?.isInSiteHierarchy, value?.outValue?.isUnderClientStructure, value?.outValue?.serial]);

  useEffect(() => {
    value?.outValue?.name && setIsSearchMode(isEmptyString(value?.outValue?.name));
    value?.outValue?.category && setIsOutSerialized(value?.outValue?.category === PartTypeActionLiveSearchItemCategoryCode.SerialiseComponent || value?.outValue?.category === PartTypeActionLiveSearchItemCategoryCode.SerialiseDevice);
    value?.outValue?.partTypeId && outValueUploadedImagesQuery.refetch();
  }, [value?.outValue?.name, value?.outValue?.category, value?.outValue?.partTypeId]);

  useEffect(() => {
    if (value?.outValue?.name) {
      setShowOutSerialSearch(value?.outValue?.category === PartTypeActionLiveSearchItemCategoryCode.SerialiseComponent || value?.outValue?.category === PartTypeActionLiveSearchItemCategoryCode.SerialiseDevice);
    }
  }, [value?.outValue?.name]);
 
  useEffect(() => {
    if (outValueUploadedImagesQuery.data) {
      const outImages: Array<ImagePopupSlide> = outValueUploadedImagesQuery.data && outValueUploadedImagesQuery.data.map((obj) => ({
        url: obj.url
      }));
      setOutPartImages(outImages);
    }
  }, [outValueUploadedImagesQuery.data]);

  const handleOutPartTypeActionSearch = async (liveSearchSelectedItem: PartTypeActionLiveSearchItem) => { 
    setHelperText({
      ...helperText,
      out: { 
        ...helperText.out,
        partTypeHelperText: '',
        partSerialHelperText: ''
      },
    });

    if (liveSearchSelectedItem.serial1) {
      onChange({
        value: value.value,
        outValue: {
          ...value.outValue,
          id: value.outValue?.id || 0,
          partTypeId: liveSearchSelectedItem.partTypeId,
          partId: liveSearchSelectedItem.partId,
          name: liveSearchSelectedItem.partTypeName,
          category: liveSearchSelectedItem.categoryCode,
          location: liveSearchSelectedItem.locationName,
          locationId: liveSearchSelectedItem.locationId,
          condition: liveSearchSelectedItem.conditionCode,
          conditionName: liveSearchSelectedItem?.partCondition?.name,
          serial: liveSearchSelectedItem.serial1,
          sw: liveSearchSelectedItem.softwareVersion,
          hw: liveSearchSelectedItem.hardwareVersion,
          fw: liveSearchSelectedItem.firmwareVersion,
          ft: liveSearchSelectedItem.fleetTagName,
          isInSiteHierarchy: liveSearchSelectedItem.isInSiteHierarchy,
          isUnderClientStructure: liveSearchSelectedItem.isUnderClientStructure,
          isAccepted: true
        },
        inValue: value.inValue
      });
    } else {
      onChange({
        value: value.value,
        outValue: {
          ...value.outValue,
          id: value.outValue?.id || 0,
          partTypeId: liveSearchSelectedItem.partTypeId,
          ns_partId: liveSearchSelectedItem.partId,
          name: liveSearchSelectedItem.partTypeName,
          category: liveSearchSelectedItem.categoryCode,
          location: liveSearchSelectedItem.locationName,
          condition: liveSearchSelectedItem.conditionCode,
          conditionName: liveSearchSelectedItem?.partCondition?.name,
          serial: '',
          sw: liveSearchSelectedItem.softwareVersion,
          hw: liveSearchSelectedItem.hardwareVersion,
          fw: liveSearchSelectedItem.firmwareVersion,
          ft: liveSearchSelectedItem.fleetTagName,
          isAccepted: true
        },
        inValue: value.inValue
      });
    }
  };

  const handleOutPartTypeSerialSearch = (val: PartTypeActionLiveSearchItem) => {
    setHelperText({
      ...helperText,
      out: { 
        ...helperText.out,
        partTypeHelperText: '',
        partSerialHelperText: ''
      },
    });

    const updatedValue = {
      value: value.value,
      outValue: {
        ...value.outValue,
        id: value.outValue?.id || 0,
        ns_partId: NaN,
        partId: val.partId,
        location: val.locationName,
        locationId: val.locationId,
        serial: val.serial1,
        condition: val.conditionCode,
        conditionName: val?.partCondition?.name,
        sw: val.softwareVersion,
        hw: val.hardwareVersion,
        fw: val.firmwareVersion,
        ft: val.fleetTagName,
        isInSiteHierarchy: val.isInSiteHierarchy,
        isUnderClientStructure: val.isUnderClientStructure
      },
      inValue: value.inValue
    };
    onChange(updatedValue);
  };

  const handleOutPartTypeLocationSelect = (val: PartTypeLocationItemData) => {
    setHelperText({
      ...helperText,
      out: { 
        ...helperText.out,
        locationHelperText: ''
      },
    });

    const updatedValue = {
      value: value.value,
      outValue: {
        ...value.outValue,
        id: value.outValue?.id || 0,
        partId: NaN,
        serial: '',
        ns_partId: val.ns_partId,
        location: val.locationName,
        condition: val.conditionCode,
        conditionName: val?.partCondition?.name,
        ft: val.fleetTagName
      },
      inValue: value.inValue
    };

    onChange(updatedValue);
    setIsSelectOutLocationPopup(false);
  };
  
  const submitInteractions = async() => {
    if (interceptedFormsObject?.forms && interceptedFormsObject?.forms?.length > 0) {
      const { hasErrorFields, hasByPassedFields, blockInteractionBypassed, updatedForms } = validateInteractionForms(interceptedFormsObject, service, getUserPermissionsQuery.data || []);
      if (updatedForms?.length > 0 && (hasErrorFields || hasByPassedFields || blockInteractionBypassed)) {
        setInterceptedFormsObject({ ...interceptedFormsObject, forms: updatedForms });
      };
      if (hasErrorFields) {
        return;
      } else {
        const updatedFormsObject = { ...interceptedFormsObject, forms: updatedForms };
        setLocationInteractionSubmitting(true);
        await submitInteractionForms(updatedFormsObject);
        setLocationInteractionSubmitting(false);
      }
    }
  };

  return (
    <DefaultCard>
      <StyledCardContent isMobileView={isMobileView}>
        <CardTitle>DOA</CardTitle>
        {partActionData?.executionCompleted ?
          <Stack justifyContent="center" alignItems="center">
            <Typography variant={TypographyVariantProps.Subtitle1} textColor={COLORS.Gray}>
              Part Action Submitted
            </Typography>
          </Stack>
          : !interceptedFormsObject?.forms || !interceptedFormsObject?.forms?.length ?
            <OuterWrapper>
              <CloseWrapper onClick={onClose} isMobileView={isMobileView}>
                <DeleteForeverOutlined color={IconColorProps.Error} fontSize={isMobileView ? 'large' : 'medium'} />
              </CloseWrapper>
              <InnerWrapper isMobileView={isMobileView} spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1} marginTop={1} width="100%">
                  <Stack direction="column" spacing={2} width="100%">
                    <PartTypeActionSearch
                      title={showOutSerialSearch ? 'Part Type Search' : 'Part/Part Type Input'}
                      serviceData={service}
                      value={value?.outValue?.name || ''}
                      partActionType={PartActionType.DOA}
                      error={!!helperText.out.partTypeHelperText}
                      helperText={helperText.out.partTypeHelperText}
                      onChange={handleOutPartTypeActionSearch}
                      searchType={showOutSerialSearch ? 'part-type' : 'part-and-part-type'}
                    />
                    {showOutSerialSearch &&
                <PartTypeActionSearch
                  title="Part Serial Search"
                  serialiseType={value?.outValue?.category}
                  partTypeId={`${value?.outValue?.partTypeId}`}
                  value={value?.outValue?.serial || ''}
                  partActionType={PartActionType.DOA}
                  error={!!helperText.out.partSerialHelperText}
                  helperText={helperText.out.partSerialHelperText}
                  onChange={handleOutPartTypeSerialSearch}
                  searchType="part"
                />
                    }
                  </Stack>
                  {isSearchMode || <ImagePopup title="Photos" images={outPartImages} />}
                </Stack>
                {isSearchMode || 
            <>
              {isOutSerialized ||
                <>
                  <SelectButton 
                    variant={ButtonVariantProps.Secondary} 
                    color={ButtonColorProps.Success}
                    onClick={() => setIsSelectOutLocationPopup(true)}
                  >
                    Select Location
                  </SelectButton>
                  {!!helperText.out.locationHelperText && 
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="error">{LOCATION_CANNOT_EMPTY}</Alert>
                    </Grid>
                  }
                </>
              }
              {!isOutSerialized &&
                <TextField 
                  label="Qty"
                  type={TextFieldTypeProps.Number}
                  value={value?.outValue?.qty}
                  inputProps={{ type: 'number', min: 0, inputMode: 'numeric', pattern: '[0-9]*' }}
                  onChange={(val) =>{
                    onChange({
                      value: value.value,
                      outValue: {
                        ...value.outValue,
                        id: value.outValue?.id || 0,
                        qty: Number(val)
                      },
                      inValue: value.inValue
                    }, true);}}
                />
              }
            </>
                }
                {isSearchMode || 
            <>
              <Grid size={{ xs: 12 }}>
                <PartTypeActionNote
                  value={value?.outValue?.notes || ''}
                  onChange={(note) => onChange({
                    value: value.value,
                    outValue: {
                      ...value.outValue,
                      id: value.outValue?.id || 0,
                      notes: note
                    },
                    inValue: value.inValue
                  }, true)}
                />
              </Grid>
              {value?.outValue?.condition && value?.outValue?.serial && partActionConditionValidation(value.outValue.condition.toLowerCase() === PartConditionOptions.INSTALLED ? PartActionValidateType.DOAOUT : PartActionValidateType.DOAIN, value.outValue.condition, value.outValue.serial) &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{partActionConditionValidation(value.outValue.condition === PartConditionOptions.INSTALLED ? PartActionValidateType.DOAOUT : PartActionValidateType.DOAIN, value.outValue.condition, value.outValue.serial)}</Alert>
                </Grid>
              }
              {siteHierarchyWarning &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{siteHierarchyWarning}</Alert>
                </Grid>
              }
              {underClientStructureWarning &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">{underClientStructureWarning}</Alert>
                </Grid>
              }
              {!value.outValue?.isAccepted &&
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning">Exception: This part has not been reviewed and accepted by the Stock Controller. Please seek assistance prior to usage in this Service</Alert>
                </Grid>
              }
            </>
                }
              </InnerWrapper>
            </OuterWrapper>
            :
            <Grid>
              {interceptedFormsObject?.forms?.map((form, index) =>
                <FormInterceptor
                  key={index}
                  service={service}
                  value={form}
                  onChange={(editedForms) => {
                    if (repeatableFormData.templateId === 0 && repeatableFormData.templateTitle === '') {
                      const updatedForms = interceptedFormsObject?.forms.map((formData) => formData.id === editedForms.id ? editedForms : formData);
                      setInterceptedFormsObject({
                        ...interceptedFormsObject,
                        forms: updatedForms
                      });
                    }
                  }}
                  onDamagedPartSelect={(Objectkey: string, objectValue: FormData | number | MerchantDamagePartSerialMenuData) => {
                    setMerchantDamageObjectParcer(prevState => ({ ...prevState, [Objectkey]: objectValue }));
                  }}
                  partActionData={partActionData}
                  onAddForm={(templateId, templateTitle) => setRepeatableFormData({ templateId, templateTitle })}
                  isRepeatable={getFormIsRepeatableStatus(form, interceptedFormsObject.forms)}
                  forms={interceptedFormsObject.forms}
                  isMobileView={isMobileView}
                />
              )}
              <br/>
              <Grid>
                <Stack direction="row" spacing={2} alignItems="center" ml={2}>
                  <Button
                    variant={ButtonVariantProps.Primary}
                    color={ButtonColorProps.Success}
                    onClick={submitInteractions}
                    disabled={(isInteractionSubmiting && locationInteractionSubmitting) || blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms)}
                    startIcon={isInteractionSubmiting && locationInteractionSubmitting ? <Loader size={20} /> : null}
                  >
                    Submit
                  </Button>
                  {blockInteractionAvailable(permissions || [], interceptedFormsObject?.forms || []) && <Typography textColor={COLORS.Red}>{getBlockInteractionMessage(interceptedFormsObject?.forms || [])}</Typography>}
                </Stack>
              </Grid>
            </Grid>
        }
      </StyledCardContent>
      <PartLocationSelectPopup
        open={isSelectOutLocationPopup}
        onSelect={handleOutPartTypeLocationSelect}
        options={outPartLocationDataByServiceIdQuery.data || []}
        onClose={() => setIsSelectOutLocationPopup(false)}
      />
    </DefaultCard>
  );
};